<template>
  <div class="page-home min-h-screen bg-[#FFFCF9]">
    <section class="relative overflow-hidden border-b border-orange-100/70 bg-white">
      <div class="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-orange-100/60 blur-3xl"></div>
      <div class="pointer-events-none absolute right-0 top-0 h-80 w-80 rounded-full bg-[#FFE5EC]/60 blur-3xl"></div>
      <div class="relative mx-auto grid max-w-[1400px] items-center gap-10 px-4 py-14 sm:py-20 lg:grid-cols-[1.05fr_.95fr] lg:px-8 lg:py-24">
        <div>
          <span class="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-brand">
            <span class="h-2 w-2 rounded-full bg-brand"></span>
            Plan less. Celebrate more.
          </span>
          <h1 class="mt-6 max-w-3xl text-4xl font-bold leading-[1.08] tracking-[-0.035em] text-ink sm:text-5xl lg:text-6xl">
            Everything your event needs, <span class="text-brand">all in one place.</span>
          </h1>
          <p class="mt-5 max-w-2xl text-base leading-7 text-muted sm:text-lg">Create a beautiful invitation, manage every RSVP, and discover trusted venues and vendors near you—from the first idea to the final guest.</p>
          <div class="mt-8 flex flex-col gap-3 sm:flex-row">
            <RouterLink to="/register" class="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white shadow-[0_12px_28px_rgba(255,122,0,.25)] transition hover:-translate-y-0.5 hover:bg-brandDark">
              Create your event <i class="ri-arrow-right-line"></i>
            </RouterLink>
            <RouterLink to="/venues" class="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 bg-white px-6 py-3.5 text-sm font-bold text-ink transition hover:border-brand hover:text-brand">
              <i class="ri-search-line"></i> Explore venues
            </RouterLink>
          </div>
          <div class="mt-8 grid max-w-xl grid-cols-3 divide-x divide-gray-200 border-t border-gray-100 pt-6">
            <div><p class="text-xl font-bold text-ink">One hub</p><p class="mt-1 text-xs text-muted">Invites & guests</p></div>
            <div class="pl-5"><p class="text-xl font-bold text-ink">Local</p><p class="mt-1 text-xs text-muted">Venues & vendors</p></div>
            <div class="pl-5"><p class="text-xl font-bold text-ink">Real-time</p><p class="mt-1 text-xs text-muted">RSVP updates</p></div>
          </div>
        </div>
        <div class="relative mx-auto w-full max-w-[620px]">
          <div class="absolute inset-8 rounded-full bg-orange-50"></div>
          <img src="/assets/illustrations/welcome.svg" alt="People welcoming guests to an event" class="relative z-10 mx-auto h-auto w-full object-contain" />
          <div class="absolute bottom-2 left-0 z-20 rounded-2xl border border-orange-100 bg-white/95 p-4 shadow-card backdrop-blur sm:left-4">
            <div class="flex items-center gap-3"><span class="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-brand"><i class="ri-calendar-check-line text-xl"></i></span><div><p class="text-sm font-bold text-ink">Your event, organized</p><p class="text-xs text-muted">Guests, vendors and updates</p></div></div>
          </div>
        </div>
      </div>
    </section>

    <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8 lg:py-16">
      <div class="text-center">
        <p class="text-xs font-bold uppercase tracking-[0.2em] text-brand">Simple from start to finish</p>
        <h2 class="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">One smooth flow for every celebration</h2>
        <p class="mx-auto mt-3 max-w-2xl text-sm leading-6 text-muted sm:text-base">No scattered spreadsheets or endless group chats. Keep the people, places and details together.</p>
      </div>
      <div class="mt-9 grid gap-5 md:grid-cols-3">
        <article v-for="step in journeySteps" :key="step.title" class="group rounded-[28px] border border-orange-100 bg-white p-6 shadow-[0_10px_35px_rgba(11,33,73,.05)] transition hover:-translate-y-1 hover:shadow-card">
          <div class="flex items-center justify-between"><span class="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-2xl text-brand"><i :class="step.icon"></i></span><span class="text-4xl font-bold text-orange-100">{{ step.number }}</span></div>
          <h3 class="mt-5 text-lg font-bold text-ink">{{ step.title }}</h3>
          <p class="mt-2 text-sm leading-6 text-muted">{{ step.copy }}</p>
        </article>
      </div>
    </section>

    <section class="overflow-hidden bg-ink text-white">
      <div class="mx-auto grid max-w-[1400px] items-center gap-12 px-4 py-14 lg:grid-cols-[.95fr_1.05fr] lg:px-8 lg:py-20">
        <div class="relative mx-auto w-full max-w-[520px] px-3 py-4 sm:px-10">
          <div class="absolute left-0 top-12 h-40 w-40 rounded-full bg-brand/20 blur-3xl"></div>
          <div class="absolute bottom-2 right-0 h-48 w-48 rounded-full bg-[#FF385C]/20 blur-3xl"></div>
          <div class="relative overflow-hidden rounded-[32px] border border-white/15 bg-white p-3 text-ink shadow-[0_30px_80px_rgba(0,0,0,.32)] sm:p-4">
            <div class="relative overflow-hidden rounded-[24px] bg-gradient-to-br from-orange-50 via-[#fff7f2] to-[#FFE5EC] px-5 pb-5 pt-4 sm:px-7 sm:pb-7">
              <div class="flex items-center justify-between"><span class="rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-brand">You’re invited</span><i class="ri-share-forward-line text-lg text-ink"></i></div>
              <img src="/assets/illustrations/team-collaboration.svg" alt="Friends celebrating together" class="mx-auto mt-1 h-40 w-full object-contain sm:h-48" />
              <p class="text-xs font-bold uppercase tracking-[0.18em] text-brand">Saturday · 6:30 PM</p>
              <h3 class="mt-2 text-2xl font-bold tracking-tight text-ink sm:text-3xl">Maya’s birthday evening</h3>
              <p class="mt-2 text-sm leading-6 text-muted">Good food, favorite people, and one very happy reason to celebrate.</p>
              <div class="mt-5 grid gap-2 text-xs sm:grid-cols-2"><div class="flex items-center gap-2 rounded-xl bg-white/80 p-3"><i class="ri-calendar-event-fill text-lg text-brand"></i><span><strong class="block text-ink">20 September</strong><span class="text-muted">6:30 PM onwards</span></span></div><div class="flex items-center gap-2 rounded-xl bg-white/80 p-3"><i class="ri-map-pin-2-fill text-lg text-brand"></i><span><strong class="block text-ink">The Garden Room</strong><span class="text-muted">View directions</span></span></div></div>
              <div class="mt-5 flex gap-2" aria-label="RSVP preview"><span class="flex-1 rounded-xl bg-brand px-3 py-3 text-center text-xs font-bold text-white">I’ll be there</span><span class="flex-1 rounded-xl border border-slate-200 bg-white px-3 py-3 text-center text-xs font-bold text-ink">Can’t make it</span></div>
            </div>
          </div>
          <div class="absolute -bottom-1 left-0 hidden items-center gap-3 rounded-2xl border border-white/20 bg-white p-3 text-ink shadow-xl sm:flex"><span class="flex h-10 w-10 items-center justify-center rounded-xl bg-green-50 text-green-600"><i class="ri-user-follow-line text-xl"></i></span><div><p class="text-sm font-bold">24 attending</p><p class="text-xs text-muted">Responses update live</p></div></div>
          <div class="absolute right-0 top-16 hidden items-center gap-2 rounded-full border border-white/20 bg-white px-3 py-2 text-xs font-bold text-ink shadow-xl sm:flex"><i class="ri-play-circle-fill text-lg text-brand"></i> Video added</div>
        </div>

        <div>
          <p class="text-xs font-bold uppercase tracking-[0.2em] text-orange-300">Invitations that feel personal</p>
          <h2 class="mt-3 max-w-2xl text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">Tell the story before the event even begins.</h2>
          <p class="mt-5 max-w-2xl text-base leading-7 text-slate-300">Build an invitation around the personality of your event. Bring together the essential details and richer media in one experience guests can open from any shared link.</p>
          <div class="mt-7 grid gap-4 sm:grid-cols-2">
            <div class="flex gap-3"><span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-orange-300"><i class="ri-image-add-line text-xl"></i></span><div><h3 class="text-sm font-bold">Make it unmistakably yours</h3><p class="mt-1 text-sm leading-6 text-slate-400">Combine event details with photos, posters, GIFs, uploaded videos, or video links.</p></div></div>
            <div class="flex gap-3"><span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-orange-300"><i class="ri-link-m text-xl"></i></span><div><h3 class="text-sm font-bold">Share one simple link</h3><p class="mt-1 text-sm leading-6 text-slate-400">Guests can open the invitation, see updates, and respond from the same event page.</p></div></div>
          </div>
          <RouterLink to="/register" class="mt-8 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-brandDark">Create an invitation <i class="ri-arrow-right-line"></i></RouterLink>
        </div>
      </div>
    </section>

    <section class="mx-auto max-w-[1400px] px-4 py-14 lg:px-8 lg:py-20">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div><p class="text-xs font-bold uppercase tracking-[0.2em] text-brand">After you press send</p><h2 class="mt-3 text-3xl font-bold tracking-tight text-ink sm:text-4xl">The invitation keeps working for you.</h2></div>
        <p class="max-w-lg text-sm leading-6 text-muted sm:text-right">Keep responses, guest activity, event media, and useful updates connected to the same celebration.</p>
      </div>
      <div class="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <article v-for="feature in invitationFeatures" :key="feature.title" class="rounded-[26px] border border-slate-100 bg-white p-5 shadow-[0_10px_32px_rgba(11,33,73,.05)] transition hover:-translate-y-1 hover:border-orange-100 hover:shadow-card">
          <span class="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-2xl text-brand"><i :class="feature.icon"></i></span>
          <h3 class="mt-5 text-base font-bold text-ink">{{ feature.title }}</h3>
          <p class="mt-2 text-sm leading-6 text-muted">{{ feature.copy }}</p>
        </article>
      </div>
    </section>

    <section class="mx-auto max-w-[1400px] px-4 pb-6 lg:px-8">
      <div class="mb-4 flex items-center justify-between">
        <RouterLink :to="venuesLink" class="group inline-flex items-center gap-2">
          <div><p class="text-xs font-bold uppercase tracking-[0.16em] text-brand">Discover nearby</p><h2 class="mt-1 text-2xl font-bold text-ink transition-colors group-hover:text-brand">
            Popular venues
          </h2></div>
          <i class="ri-arrow-right-s-line pt-1 text-lg text-ink transition-all group-hover:translate-x-1 group-hover:text-brand" aria-hidden="true"></i>
        </RouterLink>
        <div class="flex items-center gap-2">
          <button class="nav-btn prev prev-venues" aria-label="Previous">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <button class="nav-btn next next-venues" aria-label="Next">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>

      <div v-if="!hasSelectedLocation" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white px-6 py-10 text-center">
        <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#fff1e8] text-brand">
          <i class="ri-map-pin-search-line text-2xl"></i>
        </span>
        <p class="mt-4 text-lg font-semibold text-ink">Choose location to view the venues/vendors</p>
        <p class="mt-2 text-sm text-muted">Use the navbar search to pick a location and browse nearby listings.</p>
      </div>
      <div v-else-if="loading" class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Loading venues">
        <div v-for="index in 4" :key="`venue-skeleton-${index}`" class="overflow-hidden rounded-[22px] border border-slate-100 bg-white"><div class="h-44 animate-pulse bg-slate-100"></div><div class="space-y-3 p-4"><div class="h-4 w-2/3 animate-pulse rounded bg-slate-100"></div><div class="h-3 w-full animate-pulse rounded bg-slate-100"></div><div class="h-3 w-4/5 animate-pulse rounded bg-slate-100"></div></div></div>
      </div>
      <div v-else-if="error" class="rounded-[24px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
        {{ error }}
      </div>
      <div v-else-if="venueItems.length">
        <Swiper
          class="stay-swiper"
          :modules="[Navigation]"
          :slides-per-view="1.2"
          :space-between="16"
          :navigation="{ prevEl: '.prev-venues', nextEl: '.next-venues' }"
          :breakpoints="{
            640: { slidesPerView: 2.2 },
            768: { slidesPerView: 3.1 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 4 },
            1536: { slidesPerView: 4 }
          }"
        >
          <SwiperSlide v-for="item in venueItems" :key="item.id">
            <article class="stay-card h-full">
              <RouterLink
                :to="{ name: 'venue-invite-detail', params: { venueId: item.id }, query: venueDetailQuery(item) }"
                class="block"
              >
                <div class="stay-img">
                  <img v-if="item.img" :src="item.img" :alt="item.title">
                  <div v-else class="placeholder-cover">
                    {{ item.title.slice(0, 1) }}
                  </div>
                  <span v-if="item.badge" class="badge">{{ item.badge }}</span>
                  <span v-if="item.distanceLabel" class="distance-chip"><i class="ri-navigation-fill" aria-hidden="true"></i>{{ item.distanceLabel }}</span>
                </div>
              </RouterLink>
                <div class="stay-meta">
                  <RouterLink :to="{ name: 'venue-invite-detail', params: { venueId: item.id }, query: venueDetailQuery(item) }" class="title transition hover:text-brand">
                    {{ item.title }}
                  </RouterLink>
                  <p class="price">{{ item.subtitle }}</p>
                  <p class="rating address-line">
                    <i class="ri-map-pin-line text-brand"></i>
                    <span>
                      {{ displayAddress(item, 'venue') }}
                      <button
                        v-if="shouldTruncateAddress(item.location)"
                        type="button"
                        class="address-toggle"
                        @click="toggleAddress(item, 'venue')"
                      >
                        {{ isAddressExpanded(item, 'venue') ? 'Less' : 'More' }}
                      </button>
                    </span>
                  </p>
                </div>
              </article>
          </SwiperSlide>
        </Swiper>
      </div>
      <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm text-muted">
        No venues found.
      </div>
    </section>

    <section class="mx-auto max-w-[1400px] px-4 pb-16 pt-8 lg:px-8">
      <div class="mb-4 flex items-center justify-between">
        <RouterLink :to="vendorsLink" class="group inline-flex items-center gap-2">
          <div><p class="text-xs font-bold uppercase tracking-[0.16em] text-brand">Trusted professionals</p><h2 class="mt-1 text-2xl font-bold text-ink transition-colors group-hover:text-brand">Popular vendors</h2></div>
          <i class="ri-arrow-right-s-line pt-1 text-lg text-ink transition-all group-hover:translate-x-1 group-hover:text-brand" aria-hidden="true"></i>
        </RouterLink>
        <div class="flex items-center gap-2">
          <button class="nav-btn prev prev-vendors" aria-label="Previous vendors">
            <i class="ri-arrow-left-s-line"></i>
          </button>
          <button class="nav-btn next next-vendors" aria-label="Next vendors">
            <i class="ri-arrow-right-s-line"></i>
          </button>
        </div>
      </div>

      <div v-if="!hasSelectedLocation" class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm text-muted">
        Choose location to view vendors.
      </div>
      <div v-else-if="vendorsLoading" class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4" aria-label="Loading vendors">
        <div v-for="index in 4" :key="`vendor-skeleton-${index}`" class="overflow-hidden rounded-[22px] border border-slate-100 bg-white"><div class="h-44 animate-pulse bg-slate-100"></div><div class="space-y-3 p-4"><div class="h-4 w-2/3 animate-pulse rounded bg-slate-100"></div><div class="h-3 w-full animate-pulse rounded bg-slate-100"></div><div class="h-3 w-4/5 animate-pulse rounded bg-slate-100"></div></div></div>
      </div>
      <div v-else-if="vendorsError" class="rounded-[24px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
        {{ vendorsError }}
      </div>
      <div v-else-if="vendorItems.length">
        <Swiper
          class="stay-swiper"
          :modules="[Navigation]"
          :slides-per-view="1.2"
          :space-between="16"
          :navigation="{ prevEl: '.prev-vendors', nextEl: '.next-vendors' }"
          :breakpoints="{
            640: { slidesPerView: 2.2 },
            768: { slidesPerView: 3.1 },
            1024: { slidesPerView: 4 },
            1280: { slidesPerView: 4 },
            1536: { slidesPerView: 4 }
          }"
        >
          <SwiperSlide v-for="item in vendorItems" :key="item.id">
            <article class="stay-card h-full">
              <RouterLink
                :to="{ name: 'service-vendor-invite-detail', params: { serviceVendorId: item.id }, query: serviceVendorDetailQuery(item) }"
                class="block"
              >
                <div class="stay-img">
                  <img v-if="item.img" :src="item.img" :alt="item.title">
                  <div v-else class="placeholder-cover">
                    {{ item.title.slice(0, 1) }}
                  </div>
                  <span v-if="item.badge" class="badge">{{ item.badge }}</span>
                  <span v-if="item.distanceLabel" class="distance-chip"><i class="ri-navigation-fill" aria-hidden="true"></i>{{ item.distanceLabel }}</span>
                </div>
              </RouterLink>
                <div class="stay-meta">
                  <RouterLink :to="{ name: 'service-vendor-invite-detail', params: { serviceVendorId: item.id }, query: serviceVendorDetailQuery(item) }" class="title transition hover:text-brand">
                    {{ item.title }}
                  </RouterLink>
                  <p class="price">{{ item.subtitle }}</p>
                  <p class="rating address-line">
                    <i class="ri-map-pin-line text-brand"></i>
                    <span>
                      {{ displayAddress(item, 'vendor') }}
                      <button
                        v-if="shouldTruncateAddress(item.location)"
                        type="button"
                        class="address-toggle"
                        @click="toggleAddress(item, 'vendor')"
                      >
                        {{ isAddressExpanded(item, 'vendor') ? 'Less' : 'More' }}
                      </button>
                    </span>
                  </p>
                </div>
              </article>
          </SwiperSlide>
        </Swiper>
      </div>
      <div v-else class="rounded-[24px] border border-dashed border-[#eadfd4] bg-white p-6 text-sm text-muted">
        No vendors found.
      </div>
    </section>

    <section class="bg-ink px-4 py-14 text-white lg:px-8">
      <div class="mx-auto flex max-w-[1200px] flex-col items-start justify-between gap-7 md:flex-row md:items-center">
        <div><p class="text-xs font-bold uppercase tracking-[0.2em] text-orange-300">Ready when you are</p><h2 class="mt-2 text-3xl font-bold">Turn your event idea into an invitation.</h2><p class="mt-2 max-w-xl text-sm leading-6 text-slate-300">Set the details, invite your people, and manage the day from one calm, simple workspace.</p></div>
        <RouterLink to="/register" class="shrink-0 rounded-full bg-brand px-6 py-3.5 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-brandDark">Start for free <i class="ri-arrow-right-line ml-1"></i></RouterLink>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import api from '@/services/user/api'
import { useSearchStore } from '@/stores/search'
import { useSeoMeta } from '@/utils/seo'
import 'swiper/css'
import 'swiper/css/navigation'

useSeoMeta({
  title: 'Event Invitations, RSVPs, Venues & Vendors',
  description: 'Create beautiful event invitations, manage RSVPs, discover venues, and connect with event vendors using Invi8.',
  path: '/'
})

const searchStore = useSearchStore()

const journeySteps = [
  { number: '01', icon: 'ri-draft-line', title: 'Create the invitation', copy: 'Add your date, story and event details in a polished invite that looks great on every screen.' },
  { number: '02', icon: 'ri-team-line', title: 'Bring everyone together', copy: 'Send one link, collect RSVPs, add co-hosts and keep every guest update in one place.' },
  { number: '03', icon: 'ri-store-2-line', title: 'Build your event team', copy: 'Find nearby venues and service providers, compare options and send enquiries with context.' }
]

const invitationFeatures = [
  { icon: 'ri-contacts-book-3-line', title: 'Guest management', copy: 'Add guests, organize the list, and keep every response visible from your host workspace.' },
  { icon: 'ri-checkbox-circle-line', title: 'RSVPs and feedback', copy: 'Let guests respond securely and collect the notes or feedback you enable for the event.' },
  { icon: 'ri-qr-code-line', title: 'Event QR access', copy: 'Give guests a quick way to open the event and reach its shared photo experience.' },
  { icon: 'ri-navigation-line', title: 'Arrival updates', copy: 'Help hosts coordinate by seeing when guests are on the way or have checked in.' }
]

const venues = ref([])
const serviceVendors = ref([])
const loading = ref(true)
const error = ref('')
const vendorsLoading = ref(true)
const vendorsError = ref('')
const expandedAddressIds = ref(new Set())

const latitude = computed(() => String(searchStore.lat || '').trim())
const longitude = computed(() => String(searchStore.lng || '').trim())
const locationLabel = computed(() => String(searchStore.locationText || '').trim())
const hasSelectedLocation = computed(() => Boolean(locationLabel.value || (latitude.value && longitude.value)))

const detailQuery = computed(() => ({
  location: locationLabel.value || undefined,
  latitude: latitude.value || undefined,
  longitude: longitude.value || undefined
}))

const venuesLink = computed(() => ({
  name: 'venues',
  query: detailQuery.value
}))

const vendorsLink = computed(() => ({
  name: 'vendors',
  query: detailQuery.value
}))

const normalizeVenue = (item = {}, index = 0) => ({
  id: String(item?.venue_id || item?.vendor_business_venue_id || item?.id || `venue-${index}`),
  businessId: String(item?.business_id || item?.businessId || item?.business_vendor_id || item?.vendor_business_id || '').trim(),
  vendorId: String(item?.vendor_id || item?.vendorId || item?.s_id || '').trim(),
  title: String(item?.title || item?.name || 'Untitled venue').trim(),
  img: String(item?.cover_image || item?.image || '').trim(),
  subtitle: String(item?.tag_line || item?.tagLine || 'Venue details available on inquiry.').trim(),
  location: String(item?.address || 'Location unavailable').trim(),
  distanceLabel: item?.distance ? `${Number(item.distance).toFixed(1)} km away` : '',
  badge: String(item?.type_name || item?.type || '').trim()
})

const venueItems = computed(() => venues.value.map(normalizeVenue))

const venueDetailQuery = (item = {}) => ({
  ...detailQuery.value,
  vendor_id: item.vendorId || undefined,
  business_id: item.businessId || undefined
})

const normalizeServiceVendor = (item = {}, index = 0) => {
  const photo = Array.isArray(item?.photos) ? item.photos[0] : Array.isArray(item?.gallery) ? item.gallery[0] : null
  const categoryName = String(item?.cat_name || item?.category_name || item?.service_vendor_category || item?.business_category || '').trim()
  return {
    id: String(item?.service_vendor_id || item?.vendor_id || item?.id || item?.s_id || `vendor-${index}`),
    vendorId: String(item?.vendor_id || item?.business_id || item?.vendorId || '').trim(),
    title: String(item?.brand_name || item?.brandName || item?.business_name || item?.s_business || item?.name || item?.title || 'Untitled vendor').trim(),
    img: String(item?.cover_image || item?.image || item?.s_user_img || photo?.src || photo?.url || photo?.image || photo || '').trim(),
    subtitle: String(item?.tagline || item?.tag_line || item?.tagLine || categoryName || 'Service details available on inquiry.').trim(),
    location: String(item?.address || [item?.city || item?.s_city, item?.state || item?.s_state].filter(Boolean).join(', ') || 'Location unavailable').trim(),
    distanceLabel: item?.distance ? `${Number(item.distance).toFixed(1)} km away` : '',
    badge: categoryName
  }
}

const vendorItems = computed(() => serviceVendors.value.map(normalizeServiceVendor))

const addressKey = (item, type) => `${type}:${item?.id || item?.title || item?.location || ''}`
const shouldTruncateAddress = (value) => String(value || '').trim().length > 40
const isAddressExpanded = (item, type) => expandedAddressIds.value.has(addressKey(item, type))
const displayAddress = (item, type) => {
  const address = String(item?.location || 'Location unavailable').trim()
  if (!shouldTruncateAddress(address) || isAddressExpanded(item, type)) return address
  return `${address.slice(0, 38).trim()}...`
}
const toggleAddress = (item, type) => {
  const key = addressKey(item, type)
  const next = new Set(expandedAddressIds.value)
  if (next.has(key)) next.delete(key)
  else next.add(key)
  expandedAddressIds.value = next
}

const serviceVendorDetailQuery = (item = {}) => ({
  ...detailQuery.value,
  vendor_id: item.vendorId || undefined
})

const loadVenues = async () => {
  if (!hasSelectedLocation.value) {
    venues.value = []
    loading.value = false
    error.value = ''
    return
  }
  loading.value = true
  error.value = ''
  try {
    const formData = new FormData()
    formData.append('id', '')
    formData.append('latitude', latitude.value || '')
    formData.append('longitude', longitude.value || '')
    formData.append('event_type', '')
    const { data } = await api.getVenuesInvite(formData)
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data?.list) ? data.list : []
    venues.value = items
  } catch (err) {
    error.value = err?.message || 'Failed to load venues.'
    venues.value = []
  } finally {
    loading.value = false
  }
}

const loadServiceVendors = async () => {
  if (!hasSelectedLocation.value) {
    serviceVendors.value = []
    vendorsLoading.value = false
    vendorsError.value = ''
    return
  }
  vendorsLoading.value = true
  vendorsError.value = ''
  try {
    const formData = new FormData()
    formData.append('id', '')
    formData.append('latitude', latitude.value || '')
    formData.append('longitude', longitude.value || '')
    formData.append('category', searchStore.categoryId || '')
    const { data } = await api.getServiceVendorsInvite(formData)
    const items = Array.isArray(data?.data) ? data.data : Array.isArray(data?.list) ? data.list : Array.isArray(data?.vendors) ? data.vendors : []
    serviceVendors.value = items
  } catch (err) {
    vendorsError.value = err?.message || 'Failed to load vendors.'
    serviceVendors.value = []
  } finally {
    vendorsLoading.value = false
  }
}

watch(
  () => [locationLabel.value, latitude.value, longitude.value, searchStore.categoryId],
  () => {
    loadVenues()
    loadServiceVendors()
  },
  { immediate: true }
)
</script>

<style scoped>
.stay-card {
  background: #fff;
  border: 1px solid #eaecef;
  border-radius: 22px;
  overflow: hidden;
  transition: transform 0.18s ease, border-color 0.18s ease;
  min-height: 270px;
}

.stay-card:hover {
  transform: translateY(-3px);
  border-color: #d5d9e2;
}

.stay-img {
  position: relative;
  height: 180px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5e8da 0%, #f9f1e8 54%, #efe1d1 100%);
}

.stay-img img,
.placeholder-cover {
  width: 100%;
  height: 100%;
}

.stay-img img {
  object-fit: cover;
}

.placeholder-cover {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.4rem;
  font-weight: 700;
  color: #c69225;
}

.badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.distance-chip {
  position: absolute;
  right: 12px;
  top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 700;
  color: #6b4d39;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
}

.distance-chip i {
  color: #ff7a00;
  font-size: 13px;
}

.stay-meta {
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stay-meta .title {
  display: block;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.35;
}

.stay-meta .price {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
}

.stay-meta .rating {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 13px;
  color: #0f172a;
}

.address-line span {
  min-width: 0;
  line-height: 1.45;
}

.address-toggle {
  margin-left: 4px;
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  color: #ff7a00;
  cursor: pointer;
}

.address-toggle:hover {
  color: #e56700;
}

.nav-btn {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: none;
  background: #f2f3f5;
  color: #0f172a;
  transition: transform 0.15s ease, background 0.15s ease;
}

.nav-btn:hover {
  transform: translateY(-1px);
  background: #e5e7eb;
}

.stay-swiper :global(.swiper-slide) {
  height: auto;
}
</style>
